import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { bookPlace, checkBooking, unbookRoomPlace } from 'src/api/bookings';
import {
  BackgroundData,
  DeskPlaceWithStatus,
  getPlanBackground,
  readDeskPlaceMicroCards,
  readRoomPlaceMicroCards,
  readRoomPlaceStatuses,
  ReadRoomPlaceStatusesResponse,
  RoomPlaceWithStatus,
} from 'src/api/plan';
import { BookingData, BookingObject, TimePeriod } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { getDateTime } from 'src/utils/getDateTime';
import { splitTime } from 'src/utils/splitTime';
import { toBookingObject } from 'src/utils/toBookingObject';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';
import { getEmployeeNumber } from '../user/selectors';

import { emptySlots } from './initialState';
import {
  getCurrentRoom,
  getRoomLocation,
  getRoomsState,
  getRoomsTimePeriodUTC,
} from './selectors';
import { roomsActions } from './slice';

export interface RoomsAllData {
  backgroundData: BackgroundData | undefined;
  bookingObjects: Map<string, BookingObject>;
  staticObjects: Map<string, BookingObject>;
}

export const fetchRooms = createAsyncThunk<
  RoomsAllData,
  boolean,
  ThunkConfig<string>
>(
  'rooms/fetchRooms',
  async (withBackground, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;
      const { floor } = getRoomLocation(state);
      const { from, to } = getRoomsTimePeriodUTC(state);

      const data: RoomsAllData = {
        backgroundData: undefined,
        bookingObjects: new Map(),
        staticObjects: new Map(),
      };

      const floorId = floor?.floorId;

      if (!floorId || !from || !to) return data;

      const promises: [
        Promise<RoomPlaceWithStatus[]>,
        Promise<RoomsAllData['backgroundData']>,
        Promise<DeskPlaceWithStatus[]>,
      ] = [
        readRoomPlaceMicroCards({
          floorId,
          searchFrom: from,
          searchTo: to,
          withAdEmail: true,
        }),
        withBackground
          ? getPlanBackground(floorId)
          : Promise.resolve(undefined),
        readDeskPlaceMicroCards({ floorId, searchFrom: from, searchTo: to }),
      ];

      const [rooms, backgroundData, desks] = await Promise.all(promises);

      data.backgroundData = backgroundData;
      for (const desk of desks) {
        const bookingObject = toBookingObject(desk);
        data.staticObjects.set(bookingObject.mapPlaceId, bookingObject);
      }
      for (const room of rooms) {
        const bookingObject = toBookingObject(room);
        data.bookingObjects.set(bookingObject.mapPlaceId, bookingObject);
      }

      return data;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as StoreState;

      const { floor } = getRoomLocation(state);
      const { from, to } = getRoomsTimePeriodUTC(state);

      if (!floor?.floorId || !from || !to) return false;
    },
  },
);

export interface BookRoomParams {
  time: string | undefined;
  bookedFor: string | undefined;
}

export const bookRoom = createAsyncThunk<
  boolean,
  BookRoomParams,
  ThunkConfig<string>
>('rooms/book', async ({ bookedFor, time }, { getState, dispatch }) => {
  try {
    if (!time) return false;

    await dispatch(roomsActions.validateDate());

    const state = getState() as StoreState;

    const room = getCurrentRoom(state);
    const { dateError, date, town } = getRoomsState(state);
    if (!date || dateError) return false;

    const result: TimePeriod = {
      from: undefined,
      to: undefined,
    };

    const [timeFrom, timeTo] = time.split('—');

    const [fromHours, fromMinutes] = splitTime(timeFrom);
    const [toHours, toMinutes] = splitTime(timeTo);

    const fromDate = getDateTime({
      date: new Date(date),
      hours: fromHours,
      minutes: fromMinutes,
      seconds: 0,
      milliseconds: 0,
      timezoneOffset: town?.timeZone,
      toUTC: true,
    });
    const toDate = getDateTime({
      date: new Date(date),
      hours: toHours,
      minutes: toMinutes,
      seconds: 0,
      milliseconds: 0,
      timezoneOffset: town?.timeZone,
      toUTC: true,
    });
    if (toDate < fromDate) toDate.setDate(toDate.getDate() + 1);

    result.from = fromDate.toISOString();
    result.to = toDate.toISOString();

    const { from, to } = result;

    const employeeNumber = getEmployeeNumber(state);
    const responsible = bookedFor ?? employeeNumber;

    if (!room?.id || !from || !to || !responsible) return false;

    const bookPlaceResponse = await bookPlace({
      placeId: room.id,
      dateFrom: from,
      dateTo: to,
      responsible,
    });

    if (!bookPlaceResponse.accepted)
      throw new Error(bookPlaceResponse.rejectedReason ?? defaultServerError);

    const checkBookPlaceResponse = await checkBooking({
      commandId: bookPlaceResponse.commandId,
      maxDurationExceededError: 'Не удалось обработать заявку на бронь',
    });
    if (checkBookPlaceResponse.processed) {
      if (checkBookPlaceResponse.rejectedReason)
        throw new Error(checkBookPlaceResponse.rejectedReason);

      await dispatch(sidesheetActions.close());
      await dispatch(fetchRooms(false));
      await dispatch(
        messagesActions.show({
          message: `Заявка на бронь комнаты ${room.title} отправлена. Подтверждение придёт на электронную почту.`,
        }),
      );
    }
    return true;
  } catch (e) {
    const message = (e as AxiosError).message;

    dispatch(messagesActions.show({ message }));
    return false;
  }
});

export const unbookRoom = createAsyncThunk<
  void,
  BookingData,
  ThunkConfig<string>
>('rooms/unbook', async (booking, { getState, dispatch, rejectWithValue }) => {
  try {
    const state = getState() as StoreState;

    const room = getCurrentRoom(state);

    if (!room?.id) return;

    const unbookPlaceResponse = await unbookRoomPlace({
      bookingId: booking.bookingId,
      placeId: room.id,
      dateFrom: booking.bookedFrom,
      dateTo: booking.bookedTo,
    });

    if (!unbookPlaceResponse.accepted)
      throw new Error(unbookPlaceResponse.rejectedReason ?? defaultServerError);

    const checkBookPlaceResponse = await checkBooking({
      commandId: unbookPlaceResponse.commandId,
      maxDurationExceededError: `Заявка на отмену брони ${room.title} принята. Подтверждение придёт на электронную почту.`,
    });

    if (checkBookPlaceResponse.processed) {
      await dispatch(sidesheetActions.close());
      await dispatch(fetchRooms(false));
      await dispatch(
        messagesActions.show({
          message: `Заявка на отмену брони ${room.title} принята. Подтверждение придёт на электронную почту.`,
        }),
      );
    }
  } catch (e) {
    const message = (e as AxiosError).message;

    dispatch(messagesActions.show({ message }));
    return rejectWithValue((e as AxiosError).message);
  }
});

export const fetchRoomStatuses = createAsyncThunk<
  ReadRoomPlaceStatusesResponse,
  void,
  ThunkConfig<string>
>(
  'rooms/fetchRoomStatuses',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as StoreState;
      const { date, roomId, town } = getRoomsState(state);

      if (!date || !roomId) return emptySlots;

      const fromDate = getDateTime({
        date: new Date(date),
        timezoneOffset: town?.timeZone,
        hours: 6,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
        toUTC: true,
      });

      const toDate = getDateTime({
        date: new Date(date),
        timezoneOffset: town?.timeZone,
        hours: 22,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
        toUTC: true,
      });

      const slots = await readRoomPlaceStatuses({
        placeId: +roomId,
        searchFrom: fromDate.toISOString(),
        searchTo: toDate.toISOString(),
      });

      return slots;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as StoreState;

      const { date, roomId, town } = getRoomsState(state);

      if (!date || !roomId || !town) return false;
    },
  },
);
